import type { CSSProperties } from "react";
import { agent as agentIdentity, app as appIdentity, type Hue } from "@/lib/identity";
import type { GlyphName } from "@/lib/glyphs";
import { useBrandLogos } from "@/lib/brandLogos";
import { BrandLogo } from "./BrandLogo";
import { Glyph } from "./Glyph";

interface BaseProps {
  size?: number;
  className?: string;
  style?: CSSProperties;
  title?: string;
}

interface TileProps extends BaseProps {
  hue: Hue;
  glyph: GlyphName;
  round?: boolean;
  radius?: number;
  dashed?: boolean;
  faded?: boolean;
  border?: number;
  beat?: boolean;
  /**
   * Draw the tile in neutral ink instead of its hue. Identity tiles (agents, connected
   * systems) are neutral, because colour in ZeaOps means state.
   */
  neutral?: boolean;
}

/** A squircle for a connected system, or a circle (round) for an agent. */
export function Tile({
  hue,
  glyph,
  size = 36,
  round,
  radius,
  dashed,
  faded,
  border,
  beat,
  neutral,
  className,
  style,
  title,
}: TileProps) {
  // Graphite is the identity colour of every agent and system: draw it in theme ink.
  if (hue === "graphite") neutral = true;
  const classes = [
    "zh-tile",
    neutral ? "zh-a-ink" : `zh-a-${hue}`,
    round ? "zh-orb" : "",
    beat ? "zh-beat" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <span
      className={classes}
      title={title}
      aria-hidden={title ? undefined : true}
      style={{
        width: size,
        height: size,
        borderRadius: round ? "50%" : (radius ?? Math.round(size * 0.31)),
        borderStyle: dashed ? "dashed" : undefined,
        borderWidth: border,
        opacity: faded ? 0.5 : undefined,
        ...style,
      }}
    >
      <Glyph
        name={glyph}
        hue={neutral ? undefined : hue}
        color={neutral ? "var(--ink2)" : undefined}
        size={Math.round(size * 0.45)}
        strokeWidth={size < 26 ? 2.8 : 2.2}
      />
    </span>
  );
}

/**
 * A connected system: its own brand logo on a neutral tile, or its glyph where no
 * open-licensed logo exists.
 */
export function AppTile({
  app,
  size = 36,
  radius,
  dashed,
  faded,
  border,
  className,
  style,
  title,
}: BaseProps & {
  app: string;
  dashed?: boolean;
  faded?: boolean;
  radius?: number;
  border?: number;
}) {
  const id = appIdentity(app);
  const logos = useBrandLogos();
  if (!logos?.brandIcon(app)) {
    return (
      <Tile
        hue={id.hue}
        glyph={id.glyph}
        neutral
        size={size}
        radius={radius}
        dashed={dashed}
        faded={faded}
        border={border}
        className={className}
        style={style}
        title={title}
      />
    );
  }
  return (
    <span
      className={["zh-tile", "zh-a-ink", "zh-brand", className ?? ""].filter(Boolean).join(" ")}
      title={title}
      aria-hidden={title ? undefined : true}
      style={{
        width: size,
        height: size,
        borderRadius: radius ?? Math.round(size * 0.31),
        borderStyle: dashed ? "dashed" : undefined,
        borderWidth: border,
        opacity: faded ? 0.5 : undefined,
        ...style,
      }}
    >
      <BrandLogo brand={app} size={Math.round(size * 0.56)} />
    </span>
  );
}

export function AgentOrb({
  agent,
  ...rest
}: BaseProps & { agent: string; beat?: boolean; border?: number }) {
  const id = agentIdentity(agent);
  return <Tile hue={id.hue} glyph={id.glyph} round neutral {...rest} />;
}
